/**
 * RiskReadModelPage — Issue #400.
 *
 * 随访疗效 / 投诉 / 合规复核 统一风险事件 read-model 管理页：
 *   - 列表（最新快照）+ 生成 + 复核 + 归档 + 导出
 *   - 详情（汇总指标 + 风险事件明细 + 客户层汇总 + 数据源接入状态）
 *   - 同一客户跨随访 / 投诉 / 合规事件归并查看
 *
 * 约束:
 *   - 所有数据通过 @jinzhuan/api-client；严禁前端伪造快照、事件或聚合。
 *   - 后端若以 `[restricted]` 返回（无 :reveal），前端展示「需 reveal」占位。
 *   - 导出走 authenticated fetch；CSV / XLSX 首行含审计水印（后端写入）。
 *   - 未授予 :read_full 时后端返回空页 / 403；前端显示「未授权查看全租户风险事件」。
 */
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  canArchiveRiskReadModel,
  canExportRiskReadModel,
  canGenerateRiskReadModel,
  canRevealRiskReadModel,
  canReviewRiskReadModel,
  canSeeRiskReadModel,
} from '@/lib/riskReadModelPermissions';
import {
  downloadRiskReadModelExport,
  useArchiveRiskReadModelSnapshot,
  useGenerateRiskReadModelSnapshot,
  useReviewRiskReadModelSnapshot,
  useRiskReadModelEvents,
  useRiskReadModelSnapshotDetail,
  useRiskReadModelSnapshots,
  useRiskReadModelSourceStatus,
  type RiskReadModelCustomerRollupDto,
  type RiskReadModelEventRowDto,
  type RiskReadModelLevel,
  type RiskReadModelSlaStatus,
  type RiskReadModelSourceModule,
  type RiskReadModelStatus,
} from '@/hooks/useRiskReadModel';

const RESTRICTED_MARKER = '[restricted]';

function MaskedText({ value }: { value: string | undefined }) {
  if (!value) return <span className="text-gray-400">—</span>;
  if (value === RESTRICTED_MARKER) {
    return <span className="text-xs italic text-amber-600">[需 reveal]</span>;
  }
  return <span className="tabular-nums">{value}</span>;
}

function LevelBadge({ level }: { level: string }) {
  const color =
    level === 'critical'
      ? 'text-red-700'
      : level === 'high'
      ? 'text-red-600'
      : level === 'medium'
      ? 'text-amber-600'
      : 'text-gray-600';
  return <span className={`text-xs font-medium ${color}`}>{level}</span>;
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'open') {
    return <span className="text-xs font-medium text-blue-700">open</span>;
  }
  if (status === 'in_progress') {
    return (
      <span className="text-xs font-medium text-indigo-700">in_progress</span>
    );
  }
  if (status === 'waiting') {
    return <span className="text-xs font-medium text-amber-700">waiting</span>;
  }
  if (status === 'resolved' || status === 'connected' || status === 'all_connected') {
    return <span className="text-xs font-medium text-green-700">{status}</span>;
  }
  if (status === 'partial' || status === 'pending_source' || status === 'pending') {
    return <span className="text-xs font-medium text-amber-600">{status}</span>;
  }
  if (status === 'cancelled' || status === 'not_connected') {
    return <span className="text-xs font-medium text-gray-500">{status}</span>;
  }
  return <span className="text-xs font-medium text-gray-500">{status}</span>;
}

function SlaBadge({ sla }: { sla: string }) {
  if (sla === 'overdue') {
    return <span className="text-xs font-medium text-red-700">overdue</span>;
  }
  if (sla === 'due_soon') {
    return <span className="text-xs font-medium text-amber-600">due_soon</span>;
  }
  if (sla === 'on_track') {
    return <span className="text-xs font-medium text-green-700">on_track</span>;
  }
  return <span className="text-xs font-medium text-gray-400">n/a</span>;
}

const SOURCE_MODULE_LABELS: Readonly<Record<string, string>> = {
  follow_up_outcome_incident: '疗效不达预期',
  complaint_work_order: '投诉工单',
  compliance_recheck_task: '合规复核',
  follow_up_report_retraction: '健康报告退回',
};

interface EventsTableProps {
  rows: ReadonlyArray<RiskReadModelEventRowDto>;
}

function EventsTable({ rows }: EventsTableProps) {
  if (rows.length === 0) {
    return (
      <div className="rounded border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-500">
        当前筛选下暂无风险事件
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-xs text-gray-600">
          <tr>
            <th className="px-3 py-2 text-left">来源</th>
            <th className="px-3 py-2 text-left">风险事件 ID</th>
            <th className="px-3 py-2 text-left">客户短码</th>
            <th className="px-3 py-2 text-left">订单短码</th>
            <th className="px-3 py-2 text-left">风险等级</th>
            <th className="px-3 py-2 text-left">状态</th>
            <th className="px-3 py-2 text-left">责任人</th>
            <th className="px-3 py-2 text-left">SLA</th>
            <th className="px-3 py-2 text-left">触发摘要</th>
            <th className="px-3 py-2 text-left">最近动作</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-gray-50">
              <td className="px-3 py-2">
                {SOURCE_MODULE_LABELS[r.sourceModule] ?? r.sourceModule}
              </td>
              <td className="px-3 py-2 font-mono text-xs">{r.sourceId}</td>
              <td className="px-3 py-2">
                <MaskedText value={r.customerCode} />
              </td>
              <td className="px-3 py-2 text-xs text-gray-700">
                {r.orderCode ?? '—'}
              </td>
              <td className="px-3 py-2">
                <LevelBadge level={r.riskLevel} />
              </td>
              <td className="px-3 py-2">
                <StatusBadge status={r.status} />
              </td>
              <td className="px-3 py-2">
                <MaskedText value={r.responsibleUserCode} />
              </td>
              <td className="px-3 py-2">
                <div className="flex flex-col">
                  <SlaBadge sla={r.slaStatus} />
                  {r.slaDueAt && (
                    <span className="text-xs text-gray-500">
                      {new Date(r.slaDueAt).toLocaleString('zh-CN')}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-3 py-2 max-w-xs truncate text-xs text-gray-700">
                <MaskedText value={r.triggerSummary} />
              </td>
              <td className="px-3 py-2 max-w-xs truncate text-xs text-gray-700">
                <MaskedText value={r.latestActionSummary} />
                {r.latestActionAt && (
                  <div className="text-xs text-gray-500">
                    {new Date(r.latestActionAt).toLocaleString('zh-CN')}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface RollupTableProps {
  rows: ReadonlyArray<RiskReadModelCustomerRollupDto>;
}

function RollupTable({ rows }: RollupTableProps) {
  if (rows.length === 0) {
    return (
      <div className="rounded border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-500">
        当前快照内暂无客户层汇总
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-xs text-gray-600">
          <tr>
            <th className="px-3 py-2 text-left">客户短码</th>
            <th className="px-3 py-2 text-right">事件总数</th>
            <th className="px-3 py-2 text-right">开放事件</th>
            <th className="px-3 py-2 text-right">逾期事件</th>
            <th className="px-3 py-2 text-left">最高风险</th>
            <th className="px-3 py-2 text-left">最严 SLA</th>
            <th className="px-3 py-2 text-left">来源分布</th>
            <th className="px-3 py-2 text-left">最近事件</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-gray-50">
              <td className="px-3 py-2 font-medium">
                <MaskedText value={r.customerCode} />
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {r.totalEventCount}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-blue-700">
                {r.openEventCount}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-red-700">
                {r.overdueEventCount}
              </td>
              <td className="px-3 py-2">
                <LevelBadge level={r.highestRiskLevel} />
              </td>
              <td className="px-3 py-2">
                <SlaBadge sla={r.worstSlaStatus} />
              </td>
              <td className="px-3 py-2 text-xs text-gray-600">
                {r.bySourceOpenJson ?? '—'}
              </td>
              <td className="px-3 py-2 text-xs text-gray-600">
                {r.latestEventAt
                  ? new Date(r.latestEventAt).toLocaleString('zh-CN')
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function RiskReadModelPage(): React.ReactElement {
  const { permissions } = useAuth();
  const granted = useMemo(() => permissions ?? [], [permissions]);
  const canSee = canSeeRiskReadModel(granted);
  const canGenerate = canGenerateRiskReadModel(granted);
  const canReview = canReviewRiskReadModel(granted);
  const canArchive = canArchiveRiskReadModel(granted);
  const canExport = canExportRiskReadModel(granted);
  const canReveal = canRevealRiskReadModel(granted);

  const snapshots = useRiskReadModelSnapshots();
  const generate = useGenerateRiskReadModelSnapshot();
  const review = useReviewRiskReadModelSnapshot();
  const archive = useArchiveRiskReadModelSnapshot();
  const detail = useRiskReadModelSnapshotDetail();
  const sourceStatus = useRiskReadModelSourceStatus();
  const events = useRiskReadModelEvents();

  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(
    null,
  );
  const [generateNotes, setGenerateNotes] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [exportReason, setExportReason] = useState('');
  const [exportFormat, setExportFormat] = useState<'csv' | 'xlsx'>('csv');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Event filters
  const [filterSourceModule, setFilterSourceModule] = useState<string>('');
  const [filterRiskLevel, setFilterRiskLevel] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterSlaStatus, setFilterSlaStatus] = useState<string>('');
  const [filterCustomerCode, setFilterCustomerCode] = useState<string>('');

  const fetchSnapshotList = snapshots.fetchList;
  const fetchDetail = detail.fetchDetail;
  const fetchStatus = sourceStatus.fetchStatus;
  const fetchEvents = events.fetchRows;
  const runGenerate = generate.generate;
  const runReview = review.review;
  const runArchive = archive.archive;

  useEffect(() => {
    if (canSee) {
      void fetchSnapshotList({ page: 1, limit: 20 });
    }
  }, [canSee, fetchSnapshotList]);

  useEffect(() => {
    if (snapshots.data && snapshots.data.data.length > 0 && !selectedSnapshotId) {
      const first = snapshots.data.data[0];
      if (first) {
        setSelectedSnapshotId(first.id);
      }
    }
  }, [snapshots.data, selectedSnapshotId]);

  useEffect(() => {
    if (selectedSnapshotId) {
      void fetchDetail(selectedSnapshotId);
      void fetchStatus(selectedSnapshotId);
    }
  }, [selectedSnapshotId, fetchDetail, fetchStatus]);

  const handleGenerate = useCallback(async () => {
    setActionError(null);
    const created = await runGenerate(generateNotes.trim());
    if (created) {
      await fetchSnapshotList({ page: 1, limit: 20 });
      setSelectedSnapshotId(created.id);
      setGenerateNotes('');
    } else if (generate.error) {
      setActionError(generate.error.message);
    }
  }, [runGenerate, generateNotes, fetchSnapshotList, generate.error]);

  const handleReview = useCallback(async () => {
    if (!selectedSnapshotId || !detail.data) return;
    setActionError(null);
    const updated = await runReview(
      selectedSnapshotId,
      detail.data.recordVersion,
      reviewNotes.trim() || undefined,
    );
    if (updated) {
      await fetchDetail(selectedSnapshotId);
      await fetchSnapshotList({ page: 1, limit: 20 });
      setReviewNotes('');
    } else if (review.error) {
      setActionError(review.error.message);
    }
  }, [
    runReview,
    selectedSnapshotId,
    detail.data,
    reviewNotes,
    fetchDetail,
    fetchSnapshotList,
    review.error,
  ]);

  const handleArchive = useCallback(async () => {
    if (!selectedSnapshotId || !detail.data) return;
    setActionError(null);
    const updated = await runArchive(
      selectedSnapshotId,
      detail.data.recordVersion,
    );
    if (updated) {
      await fetchDetail(selectedSnapshotId);
      await fetchSnapshotList({ page: 1, limit: 20 });
    } else if (archive.error) {
      setActionError(archive.error.message);
    }
  }, [
    runArchive,
    selectedSnapshotId,
    detail.data,
    fetchDetail,
    fetchSnapshotList,
    archive.error,
  ]);

  const handleExport = useCallback(async () => {
    if (!selectedSnapshotId) return;
    setExporting(true);
    setExportError(null);
    try {
      await downloadRiskReadModelExport({
        snapshotId: selectedSnapshotId,
        reason: exportReason || '日常风险事件审计',
        format: exportFormat,
      });
    } catch (err) {
      setExportError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  }, [exportFormat, exportReason, selectedSnapshotId]);

  const handleApplyEventFilter = useCallback(() => {
    if (!selectedSnapshotId) return;
    void fetchEvents(selectedSnapshotId, {
      sourceModule: (filterSourceModule || undefined) as
        | RiskReadModelSourceModule
        | undefined,
      riskLevel: (filterRiskLevel || undefined) as RiskReadModelLevel | undefined,
      status: (filterStatus || undefined) as RiskReadModelStatus | undefined,
      slaStatus: (filterSlaStatus || undefined) as RiskReadModelSlaStatus | undefined,
      customerCode: filterCustomerCode.trim() || undefined,
    });
  }, [
    fetchEvents,
    selectedSnapshotId,
    filterSourceModule,
    filterRiskLevel,
    filterStatus,
    filterSlaStatus,
    filterCustomerCode,
  ]);

  const displayedEventRows = useMemo(
    () => events.data?.data ?? detail.data?.eventRows ?? [],
    [events.data, detail.data],
  );

  if (!canSee) {
    return (
      <div className="p-6">
        <div className="rounded border border-amber-200 bg-amber-50 p-4 text-amber-700">
          当前账号未授予 risk.read_model:read 或 risk.read_model:read_full，无法查看风险事件 read-model。
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold text-gray-900">
          风险事件统一读模型（随访 / 投诉 / 合规）
        </h1>
        <p className="text-sm text-gray-600">
          汇总疗效不达预期事件、投诉工单、合规复核任务、健康报告退回；按客户归并查看。本页面只读源记录；状态写入仍由各源模块负责。
        </p>
        {!canReveal && (
          <p className="text-xs text-amber-600">
            当前账号未授予 :reveal — 客户短码、责任人短码、触发摘要、最近动作明文将显示为「需 reveal」。
          </p>
        )}
      </header>

      <section className="space-y-3 rounded border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-600">备注</label>
            <input
              type="text"
              value={generateNotes}
              onChange={(e) => setGenerateNotes(e.target.value)}
              placeholder="例：周风险事件汇总快照"
              className="mt-1 w-72 rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </div>
          <button
            type="button"
            disabled={!canGenerate || generate.loading}
            onClick={handleGenerate}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {generate.loading ? '生成中…' : '生成新快照'}
          </button>
          <button
            type="button"
            onClick={() => void fetchSnapshotList({ page: 1, limit: 20 })}
            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700"
          >
            刷新列表
          </button>
        </div>
        {(generate.error || actionError) && (
          <div className="text-sm text-red-600">
            {actionError ?? generate.error?.message}
          </div>
        )}
      </section>

      <section className="rounded border border-gray-200 bg-white shadow-sm">
        <header className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-base font-semibold">快照列表</h2>
          {snapshots.loading && (
            <span className="text-xs text-gray-500">加载中…</span>
          )}
        </header>
        {snapshots.error && (
          <div className="px-4 py-3 text-sm text-red-600">
            {snapshots.error.message}
          </div>
        )}
        {snapshots.data && snapshots.data.data.length === 0 && (
          <div className="px-4 py-6 text-center text-sm text-gray-500">
            尚无快照。请用上方「生成新快照」按钮生成。
          </div>
        )}
        {snapshots.data && snapshots.data.data.length > 0 && (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">id</th>
                <th className="px-3 py-2 text-left">快照时间</th>
                <th className="px-3 py-2 text-left">状态</th>
                <th className="px-3 py-2 text-left">数据源</th>
                <th className="px-3 py-2 text-left">备注</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {snapshots.data.data.map((s) => (
                <tr
                  key={s.id}
                  className={
                    s.id === selectedSnapshotId
                      ? 'bg-blue-50'
                      : 'bg-white hover:bg-gray-50'
                  }
                >
                  <td className="px-3 py-2 font-mono text-xs">{s.id}</td>
                  <td className="px-3 py-2">
                    {new Date(s.snapshotTakenAt).toLocaleString('zh-CN')}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={s.status} />
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={s.sourceStatus} />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {s.notes ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setSelectedSnapshotId(s.id)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      查看
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {selectedSnapshotId && detail.data && (
        <section className="space-y-4 rounded border border-gray-200 bg-white p-4 shadow-sm">
          <header className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold">
              快照 {detail.data.id} 详情
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">
                状态: <StatusBadge status={detail.data.status} /> · 版本 {detail.data.recordVersion}
              </span>
              {canReview && detail.data.status === 'generated' && (
                <>
                  <input
                    type="text"
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="复核意见"
                    className="rounded border border-gray-300 px-2 py-1 text-xs"
                  />
                  <button
                    type="button"
                    onClick={handleReview}
                    disabled={review.loading}
                    className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700"
                  >
                    {review.loading ? '复核中…' : '标记已复核'}
                  </button>
                </>
              )}
              {canArchive &&
                (detail.data.status === 'generated' ||
                  detail.data.status === 'reviewed') && (
                  <button
                    type="button"
                    onClick={handleArchive}
                    disabled={archive.loading}
                    className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700"
                  >
                    {archive.loading ? '归档中…' : '归档'}
                  </button>
                )}
            </div>
          </header>

          {detail.data.totals && (
            <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4 lg:grid-cols-6">
              <div className="rounded border border-gray-200 p-2">
                <div className="text-xs text-gray-500">事件总数</div>
                <div className="text-lg font-semibold tabular-nums">
                  {detail.data.totals.totalEvents}
                </div>
              </div>
              <div className="rounded border border-gray-200 p-2">
                <div className="text-xs text-gray-500">开放事件</div>
                <div className="text-lg font-semibold tabular-nums text-blue-700">
                  {detail.data.totals.openCount}
                </div>
              </div>
              <div className="rounded border border-gray-200 p-2">
                <div className="text-xs text-gray-500">逾期事件</div>
                <div className="text-lg font-semibold tabular-nums text-red-700">
                  {detail.data.totals.overdueCount}
                </div>
              </div>
              <div className="rounded border border-gray-200 p-2">
                <div className="text-xs text-gray-500">即将到期</div>
                <div className="text-lg font-semibold tabular-nums text-amber-600">
                  {detail.data.totals.dueSoonCount}
                </div>
              </div>
              <div className="rounded border border-gray-200 p-2">
                <div className="text-xs text-gray-500">覆盖客户</div>
                <div className="text-lg font-semibold tabular-nums">
                  {detail.data.totals.customerCount}
                </div>
              </div>
              <div className="rounded border border-gray-200 p-2">
                <div className="text-xs text-gray-500">有开放事件客户</div>
                <div className="text-lg font-semibold tabular-nums">
                  {detail.data.totals.customersWithOpenEvents}
                </div>
              </div>
            </div>
          )}

          {canExport && (
            <div className="flex flex-wrap items-end gap-2 rounded border border-blue-100 bg-blue-50 p-3">
              <div>
                <label className="block text-xs text-gray-600">导出原因</label>
                <input
                  type="text"
                  value={exportReason}
                  onChange={(e) => setExportReason(e.target.value)}
                  placeholder="例：月度风险事件复盘"
                  className="mt-1 w-72 rounded border border-gray-300 px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600">格式</label>
                <select
                  value={exportFormat}
                  onChange={(e) =>
                    setExportFormat(e.target.value as 'csv' | 'xlsx')
                  }
                  className="mt-1 rounded border border-gray-300 px-2 py-1 text-sm"
                >
                  <option value="csv">CSV</option>
                  <option value="xlsx">XLSX</option>
                </select>
              </div>
              <button
                type="button"
                disabled={exporting}
                onClick={handleExport}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white disabled:bg-gray-300"
              >
                {exporting ? '导出中…' : '导出（含水印 + 审计）'}
              </button>
              {exportError && (
                <span className="text-xs text-red-600">{exportError}</span>
              )}
            </div>
          )}

          {sourceStatus.data && (
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-4">
              {sourceStatus.data.sources.map((s) => (
                <div
                  key={s.sourceType}
                  className="rounded border border-gray-200 p-2 text-sm"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-700">
                      {s.displayLabel}
                    </span>
                    <StatusBadge status={s.status} />
                  </div>
                  {s.reason && (
                    <div className="mt-1 text-xs text-gray-500">{s.reason}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="space-y-3">
            <h3 className="text-sm font-semibold">风险事件明细</h3>
            <div className="flex flex-wrap items-end gap-2 rounded border border-gray-200 bg-gray-50 p-3">
              <select
                value={filterSourceModule}
                onChange={(e) => setFilterSourceModule(e.target.value)}
                className="rounded border border-gray-300 px-2 py-1 text-xs"
              >
                <option value="">全部来源</option>
                <option value="follow_up_outcome_incident">疗效不达预期</option>
                <option value="complaint_work_order">投诉工单</option>
                <option value="compliance_recheck_task">合规复核</option>
                <option value="follow_up_report_retraction">报告退回</option>
              </select>
              <select
                value={filterRiskLevel}
                onChange={(e) => setFilterRiskLevel(e.target.value)}
                className="rounded border border-gray-300 px-2 py-1 text-xs"
              >
                <option value="">全部风险等级</option>
                <option value="critical">critical</option>
                <option value="high">high</option>
                <option value="medium">medium</option>
                <option value="low">low</option>
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="rounded border border-gray-300 px-2 py-1 text-xs"
              >
                <option value="">全部状态</option>
                <option value="open">open</option>
                <option value="in_progress">in_progress</option>
                <option value="waiting">waiting</option>
                <option value="resolved">resolved</option>
                <option value="cancelled">cancelled</option>
              </select>
              <select
                value={filterSlaStatus}
                onChange={(e) => setFilterSlaStatus(e.target.value)}
                className="rounded border border-gray-300 px-2 py-1 text-xs"
              >
                <option value="">全部 SLA</option>
                <option value="overdue">overdue</option>
                <option value="due_soon">due_soon</option>
                <option value="on_track">on_track</option>
                <option value="not_applicable">not_applicable</option>
              </select>
              <input
                type="text"
                value={filterCustomerCode}
                onChange={(e) => setFilterCustomerCode(e.target.value)}
                placeholder="客户短码（精确）"
                className="w-44 rounded border border-gray-300 px-2 py-1 text-xs"
              />
              <button
                type="button"
                onClick={handleApplyEventFilter}
                className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700"
              >
                应用过滤
              </button>
              <button
                type="button"
                onClick={() => {
                  setFilterSourceModule('');
                  setFilterRiskLevel('');
                  setFilterStatus('');
                  setFilterSlaStatus('');
                  setFilterCustomerCode('');
                  if (selectedSnapshotId)
                    void fetchEvents(selectedSnapshotId, {});
                }}
                className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700"
              >
                清空过滤
              </button>
            </div>
            <EventsTable rows={displayedEventRows} />
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold">客户层汇总（同一客户跨源归并）</h3>
            <RollupTable rows={detail.data.customerRollups} />
          </div>
        </section>
      )}
    </div>
  );
}
